'use client';
import { Typography, useTheme } from '@mui/material';
import React from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import { StrapiBackgroundOptions, StrapiDisplayOptions } from '@/lib/DisplayOptionsHelper';
import { useAssetFinanceCalculators } from '@/lib/hooks/useAssetFinanceCalculators';
import {
  ComponentBlocksLoanCalculators,
  Enum_Componentshareddisplayoptions_Contentswidth,
} from '@/types.d';

import {
  CalculatorInputs,
  CalculatorLayout,
  CalculatorOutputs,
  OutputComparison,
  OutputField,
  Styled2ColGrid,
  StyledColumn,
  StyledTextSlider,
  StyledToggleButtonField,
} from './components';

type LeaseVsLoanFormValues = {
  financedAmount: number;
  loanTermYears: number;
  repaymentFrequency: 'monthly' | 'fortnightly' | 'weekly';
  leaseRate: number;
  residualValue: number;
  loanRate: number;
  balloonPaymentPercent: number;
};

const LeaseVsLoan = ({ block }: { block: ComponentBlocksLoanCalculators }) => {
  const theme = useTheme();
  const { calculateLeaseVsLoan } = useAssetFinanceCalculators();

  const methods = useForm<LeaseVsLoanFormValues>({
    defaultValues: {
      financedAmount: 50000,
      loanTermYears: 5,
      repaymentFrequency: 'monthly',
      leaseRate: 7.5,
      residualValue: 10000,
      loanRate: 8.5,
      balloonPaymentPercent: 20,
    },
  });
  const { control, watch } = methods;
  const values = watch();

  const result = calculateLeaseVsLoan({
    financedAmount: values.financedAmount || 0,
    loanTermYears: values.loanTermYears || 0,
    leaseRate: values.leaseRate || 0,
    loanRate: values.loanRate || 0,
    residualValue: values.residualValue || 0,
    balloonPaymentPercent: values.balloonPaymentPercent || 0,
    repaymentFrequency: values.repaymentFrequency || 'monthly',
  });

  const comparisonLabel =
    result.cheaperOption === 'lease'
      ? `Finance Lease is cheaper by $${result.cheaperByAmount}`
      : result.cheaperOption === 'loan'
        ? `Loan is cheaper by $${result.cheaperByAmount}`
        : 'Equal finance cost';

  return (
    <FormProvider {...methods}>
      <CalculatorLayout
        title={block?.CustomCalculatorTitle ?? undefined}
        blurb={block?.CalculatorBlurb}
        backgroundOptions={(block?.CalculatorBackground ?? undefined) as StrapiBackgroundOptions}
        displayOptions={
          {
            ...block?.CalculatorDisplayOptions,
            ContentsWidth:
              block?.CalculatorDisplayOptions?.ContentsWidth ??
              Enum_Componentshareddisplayoptions_Contentswidth.Wide,
            PaddingTop: block?.CalculatorDisplayOptions?.PaddingTop ?? 'md',
            PaddingBottom: block?.CalculatorDisplayOptions?.PaddingBottom ?? 'md',
            PaddingInline: block?.CalculatorDisplayOptions?.PaddingInline ?? 'sm',
          } as StrapiDisplayOptions
        }
        rowLayout={false}
        dataComponent="LeaseVsLoan"
      >
        <CalculatorInputs title={block?.CustomInputsTitle ?? 'Select loan terms'}>
          <Controller
            name="financedAmount"
            control={control}
            render={({ field }) => (
              <StyledTextSlider
                label="Financed Amount"
                prefix="$"
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            name="loanTermYears"
            control={control}
            render={({ field }) => (
              <StyledTextSlider
                label="Loan Term"
                suffix={field.value === 1 ? ' year' : ' years'}
                value={field.value}
                minValue={1}
                maxValue={10}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            name="repaymentFrequency"
            control={control}
            render={({ field }) => (
              <StyledToggleButtonField
                label="Repayment Frequency"
                value={field.value}
                onChange={(value) =>
                  field.onChange(value as LeaseVsLoanFormValues['repaymentFrequency'])
                }
                sx={{
                  '& .MuiToggleButtonGroup-root': {
                    display: { xs: 'flex', md: 'row' },
                    flexDirection: 'row',
                  },
                }}
                options={[
                  { value: 'monthly', label: 'Monthly' },
                  { value: 'fortnightly', label: 'Fortnightly' },
                  { value: 'weekly', label: 'Weekly' },
                ]}
              />
            )}
          />
          <Styled2ColGrid>
            <StyledColumn>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  color: theme.palette.purple[900],
                  textTransform: 'uppercase',
                  fontSize: theme.typography.small.fontSize,
                }}
              >
                Finance Lease
              </Typography>
              <Controller
                name="leaseRate"
                control={control}
                render={({ field }) => (
                  <StyledTextSlider
                    label="Lease Rate"
                    suffix="%"
                    value={field.value}
                    defaultMax={30}
                    step={0.1}
                    onChange={field.onChange}
                  />
                )}
              />
              <Controller
                name="residualValue"
                control={control}
                render={({ field }) => (
                  <StyledTextSlider
                    label="Residual Value"
                    prefix="$"
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </StyledColumn>
            <StyledColumn>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  color: theme.palette.purple[900],
                  textTransform: 'uppercase',
                  fontSize: theme.typography.small.fontSize,
                }}
              >
                Chattel Mortgage / Loan
              </Typography>
              <Controller
                name="loanRate"
                control={control}
                render={({ field }) => (
                  <StyledTextSlider
                    label="Loan Rate"
                    suffix="%"
                    value={field.value}
                    defaultMax={30}
                    step={0.1}
                    onChange={field.onChange}
                  />
                )}
              />
              <Controller
                name="balloonPaymentPercent"
                control={control}
                render={({ field }) => (
                  <StyledTextSlider
                    label="Balloon Payment"
                    suffix="%"
                    value={field.value}
                    maxValue={100}
                    onChange={field.onChange}
                  />
                )}
              />
            </StyledColumn>
          </Styled2ColGrid>
        </CalculatorInputs>

        <CalculatorOutputs title={block?.CustomOutputsTitle ?? 'Lease vs Loan Summary'}>
          <OutputComparison
            columnTitles={['Lease', 'Loan']}
            rows={[
              {
                label: `Payment (${result.paymentLabel})`,
                values: [`$${result.leasePayment}`, `$${result.loanPayment}`],
              },
              {
                label: 'Finance Cost',
                values: [`$${result.leaseFinanceCost}`, `$${result.loanFinanceCost}`],
              },
            ]}
          />
          <OutputField highlight label={'Comparison'} value={comparisonLabel} />
        </CalculatorOutputs>
      </CalculatorLayout>
    </FormProvider>
  );
};

export default LeaseVsLoan;
